/**
 * The one list view every resource type renders through (GDN-2723).
 *
 * Built on FilterableDataTable so search, per-column filters, sort, and
 * pagination are standard rather than something each bespoke list
 * re-implemented — that parity is what makes migrating a bespoke list a
 * consolidation instead of a downgrade.
 *
 * The owner filter is implied by the route: at user scope this is a
 * list-for-user, at org scope a list-for-org.
 */

import { useCallback, useMemo } from 'react';
import { Alert, Box, Chip } from '@mui/material';

import {
  useGetResourceTypeSchemaQuery,
  useListResourcesByTypeQuery,
} from '../../store/hooks';
import {
  resourceDetailPath,
  type ResourceScope,
} from '../../utils/resource-routes';
import { FilterableDataTable } from '../filterable-data-table';
import { getResourceView, resourceLabel } from './registry';
import { deriveColumns, formatCell } from './schema-columns';
import type { ResourceRow } from './types';

interface ApiErrorData {
  error_code?: string;
  message?: string;
}

function errorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'data' in error) {
    const data = (error as { data?: ApiErrorData }).data;
    if (data?.message) return data.message;
  }
  return 'Could not load this resource list.';
}

function rowId(row: ResourceRow): string {
  const id = row.id;
  if (typeof id === 'string') return id;
  if (typeof id === 'number') return String(id);
  return '';
}

export interface ResourceListShellProps {
  resourceType: string;
  scope: ResourceScope;
  page: number;
  perPage: number;
  filters: Record<string, string>;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  onFiltersChange: (filters: Record<string, string>) => void;
  onSortChange: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
}

export function ResourceListShell({
  resourceType,
  scope,
  page,
  perPage,
  filters,
  sortBy,
  sortOrder,
  onPageChange,
  onPerPageChange,
  onFiltersChange,
  onSortChange,
}: ResourceListShellProps) {
  const view = getResourceView(resourceType);

  const { data, isFetching, error } = useListResourcesByTypeQuery({
    resourceType,
    organizationId: scope.organizationId,
    providerId: scope.userId,
    patientId: filters.patient_id,
    page,
    perPage,
  });
  const { data: schema } = useGetResourceTypeSchemaQuery({ resourceType });

  const items = useMemo<ResourceRow[]>(
    () => ((data?.items ?? []) as unknown[]).map(i => (i ?? {}) as ResourceRow),
    [data?.items],
  );

  // A registered type states its own columns; everything else derives them
  // from the JSON Schema, which is why an unregistered type is still browsable.
  const columns = useMemo(() => {
    const defs = view?.columns ?? deriveColumns(items, schema, resourceType);
    return [
      {
        key: 'id',
        label: 'ID',
        sortable: false,
        render: (value: unknown) =>
          value ? (
            <span className="font-mono text-xs">{String(value)}</span>
          ) : (
            <Chip size="small" label="no id" />
          ),
      },
      ...defs.map(col => ({
        key: col.key,
        label: col.label,
        sortable: col.sortable ?? false,
        render: col.render ?? ((value: unknown) => formatCell(value)),
      })),
    ];
  }, [view?.columns, items, schema, resourceType]);

  const getRowUrl = useCallback(
    (row: ResourceRow) => {
      const id = rowId(row);
      return id ? resourceDetailPath(scope, resourceType, id) : '';
    },
    [scope, resourceType],
  );

  if (error) {
    return <Alert severity="warning">{errorMessage(error)}</Alert>;
  }

  return (
    <Box>
      <FilterableDataTable<ResourceRow>
        title={resourceLabel(resourceType)}
        columns={columns}
        data={items}
        total={data?.total ?? items.length}
        loading={isFetching}
        page={page}
        perPage={perPage}
        filters={view?.filters ?? []}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onPageChange={onPageChange}
        onPerPageChange={onPerPageChange}
        onFiltersChange={onFiltersChange}
        onSortChange={onSortChange}
        getRowUrl={getRowUrl}
        emptyMessage={`No ${resourceLabel(resourceType)} records match these filters.`}
      />
    </Box>
  );
}
